import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getInit } from '@/repositories/direct_deposit_repository';
import type { Init } from '@/api/DirectDepositApi';

export const useInitStore = defineStore('init', () => {
    // TanStack Query setup
    const INIT_QUERY_KEY = ['init'] as const;

    // Query for fetching init data
    const initQuery = useQuery({
        queryKey: INIT_QUERY_KEY,
        queryFn: getInit,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        enabled: false
    });

    // Computed values for easy access to init data
    const initData = computed(() => initQuery.data.value);
    const showPrimaryAccount = computed(() => initQuery.data.value?.showPrimaryAccount ?? true);
    const showReimbursementAccount = computed(() => initQuery.data.value?.showReimbursementAccount ?? true);
    const showSecondaryAccount = computed(() => initQuery.data.value?.showSecondaryAccount ?? true);
    const hideBankAccounts = computed(() => initQuery.data.value?.hideBankAccounts ?? false);
    const message = computed(() => initQuery.data.value?.message ?? null);
    const useStripe = computed(() => initQuery.data.value?.useStripe ?? false);
    const stripePublicKey = computed(() => initQuery.data.value?.stripePublicKey ?? null);

    // Loading and error states
    const isLoading = computed(() => initQuery.isPending.value);
    const error = computed(() => initQuery.error.value);

    // Methods
    const loadInit = async () => {
        try {
            const result = await initQuery.refetch();
            return result.data;
        } catch (error) {
            console.error('Failed to load init data:', error);
            throw error;
        }
    };

    return {
        // Data
        initData,
        showPrimaryAccount,
        showReimbursementAccount,
        showSecondaryAccount,
        hideBankAccounts,
        message,
        useStripe,
        stripePublicKey,
        // States
        isLoading,
        error,
        // Methods
        loadInit,
        // Expose query for direct access if needed
        refetchInit: initQuery.refetch
    };
});
